'use client';

import { useCallback } from 'react';
import { useRouter as useNextRouter } from 'next/navigation';
// EWM-760 — type-only import of the concrete Next.js router options shape,
// instead of the `Parameters<typeof nextRouter.push>[1]` type query used
// previously. The `typeof nextRouter` type query reads (to the React
// Compiler's dependency analysis) as an extra reference to `nextRouter`
// inside the parameter's type position, which was why `push`/`replace`
// below couldn't preserve their manual `useCallback` memoization
// (react-hooks/preserve-manual-memoization). This is a type-only change —
// erased at compile time — so it has zero runtime/behavioural difference.
import type { NavigateOptions } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { useRouter as useIntlRouter } from '@/i18n/routing';
import { Locale } from '@/project.config';

// EWM-522 — programmatic in-app navigation through next-intl's router, so the
// locale prefix follows the routing config (`/{locale}/` on the shared origin,
// prefix-less on a per-locale domain). External / protocol-relative hrefs use
// the raw Next router unchanged. Public API (push/replace + the rest of the
// router) is preserved so the ~50 call sites don't change.
const isInternalHref = (href: string): boolean => href.startsWith('/') && !href.startsWith('//');

const useCustomRouter = () => {
  const intlRouter = useIntlRouter();
  const nextRouter = useNextRouter();

  const push = useCallback(
    (href: string, { locale, ...options }: { locale?: string } & NavigateOptions = {}) => {
      if (isInternalHref(href)) {
        intlRouter.push(href, { ...(locale ? { locale: locale as Locale } : {}), ...options });
      } else {
        nextRouter.push(href, options);
      }
    },
    [intlRouter, nextRouter],
  );

  const replace = useCallback(
    (href: string, { locale, ...options }: { locale?: string } & NavigateOptions = {}) => {
      if (isInternalHref(href)) {
        intlRouter.replace(href, { ...(locale ? { locale: locale as Locale } : {}), ...options });
      } else {
        nextRouter.replace(href, options);
      }
    },
    [intlRouter, nextRouter],
  );

  return { ...intlRouter, push, replace };
};

export default useCustomRouter;
